import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import NastenkaService from "./NastenkaService";
import { useToast } from "../../../components/toast/useToast";
import type {
    Nastenka,
    NastenkaFeedResponse,
    CreateNastenkaRequest,
    UpdateNastenkaRequest,
} from "../generated/models/Nastenka";

export const useNastenkaFeedQuery = () => {
    return useQuery<NastenkaFeedResponse, Error>({
        queryKey: ['nastenka'],
        queryFn: () => NastenkaService.getFeed(),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
    });
};

export const useCreateNastenkaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Nastenka, Error, CreateNastenkaRequest>({
        mutationFn: (data) => NastenkaService.createNastenka(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['nastenka'] });
            showToast("Příspěvek byl zveřejněn.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se zveřejnit příspěvek.", "error");
        },
    });
};

export const useUpdateNastenkaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Nastenka, Error, { id: number; data: UpdateNastenkaRequest }>({
        mutationFn: ({ id, data }) => NastenkaService.updateNastenka(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['nastenka'] });
            showToast("Příspěvek byl upraven.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se upravit příspěvek.", "error");
        },
    });
};

export const useDeleteNastenkaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<unknown, Error, number>({
        mutationFn: (id) => NastenkaService.deleteNastenka(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['nastenka'] });
            showToast("Příspěvek byl smazán.", "success");
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat příspěvek.", "error");
        },
    });
};

export const usePinNastenkaMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<unknown, Error, { id: number; pripnout: boolean }>({
        mutationFn: ({ id, pripnout }) =>
            pripnout ? NastenkaService.pinNastenka(id) : NastenkaService.unpinNastenka(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['nastenka'] });
        },
        onError: (error) => {
            showToast(error.message || "Nepodařilo se změnit připnutí.", "error");
        },
    });
};
